import type { CalendarEvent } from '@highlight/common'

const DAY_MS = 24 * 60 * 60 * 1000

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Bucket label for an event's day, relative to `now`: Today / Tomorrow, then
 * the weekday name — prefixed "Next" once the day falls past the end of the
 * current week (weeks end Sunday) so two "Tuesday"s can't read the same.
 */
export function getDateGroupLabel(date: Date, now: Date): string {
  const today = startOfDay(now)
  const tomorrow = new Date(today.getTime() + DAY_MS)
  const eventDate = startOfDay(date)

  if (eventDate.getTime() === today.getTime()) {
    return 'Today'
  }
  if (eventDate.getTime() === tomorrow.getTime()) {
    return 'Tomorrow'
  }

  const dayOfWeek = today.getDay()
  const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek
  const endOfWeek = new Date(today.getTime() + daysUntilSunday * DAY_MS)

  const weekday = eventDate.toLocaleDateString('en-US', { weekday: 'long' })

  return eventDate > endOfWeek ? `Next ${weekday}` : weekday
}

export interface UpcomingEventGroup {
  label: string
  events: Array<CalendarEvent>
}

/**
 * Group already-sorted events into consecutive day buckets. Runs of the same
 * label collapse into one group; a label can repeat if the sort interleaves
 * days (live-first ordering does this deliberately).
 */
export function groupUpcomingEvents(events: Array<CalendarEvent>, now: Date): Array<UpcomingEventGroup> {
  const groups: Array<UpcomingEventGroup> = []

  for (const event of events) {
    const label = getDateGroupLabel(new Date(event.start), now)
    const currentGroup = groups[groups.length - 1]
    if (!currentGroup || currentGroup.label !== label) {
      groups.push({ label, events: [event] })
    } else {
      currentGroup.events.push(event)
    }
  }

  return groups
}
